import { makeAutoObservable, reaction, type IReactionDisposer } from 'mobx'
import { nanoid } from 'nanoid'
import { isAxiosError } from 'axios'
import { showToast } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { TableStore } from 'shared/ui/TableNew'
import { logger } from 'shared/lib'
import { type IResponseAuthClient, type IResponseCreateAuthClientError } from 'entities/Auth'
import { DeveloperAuthApi } from 'entities/Auth/api'
import { AuthClientRow } from 'pages/settings/pages/developer/pages/applications/model/AuthClientRow'
import { ClientModalActions } from 'pages/settings/pages/developer/pages/applications/ui/ClientModal/ClientModalActions'
import { ClientModalContent } from 'pages/settings/pages/developer/pages/applications/ui/ClientModal/ClientModalContent'
export class ApplicationsStore {
  private _loading = false
  private _clients: IResponseAuthClient[] = []
  private readonly _tableStore: TableStore<AuthClientRow>
  private readonly _listMap = new Map<number, AuthClientRow>()
  private readonly _clientModalId = nanoid()
  private _newClientName = ''
  private _newClientRedirectUrl = ''
  private _newClientNameError = ''
  private _newClientRedirectUrlError = ''
  private _editingClientId: number | null = null
  private _clientModalSaving = false
  private readonly _revokeModalId = nanoid()
  private _revokingClientId: number | null = null
  private _revoking = false
  private _revokeModalDisposeReaction: IReactionDisposer | null = null
  allScopes: string[] = []
  selectedScopes: string[] = []
  createClientError = ''

  constructor() {
    makeAutoObservable(this)
    this._tableStore = new TableStore<AuthClientRow>({
      key: 'developer_applications',
      limit: 25,
    })
  }

  init = async () => {
    await Promise.all([this.loadScopes(), this.loadClients()])
  }

  loadScopes = async () => {
    try {
      const { data } = await DeveloperAuthApi.getApiScopes()
      this.allScopes = (data ?? []).map((scope) => scope.id)
      this.selectedScopes = this.allScopes
    } catch (e) {
      logger.error(e)
    }
  }

  loadClients = async () => {
    this._loading = true

    try {
      const { data } = await DeveloperAuthApi.getAuthClients()
      const list = data ?? []
      this._clients = list
      this._listMap.clear()
      const rows = list.map((item) => new AuthClientRow(item))
      rows.forEach((row) => this._listMap.set(Number(row.id), row))
      this._tableStore.setRows(rows)
      this._tableStore.setTotal(rows.length)
    } finally {
      this._loading = false
    }
  }

  get loading() {
    return this._loading
  }

  get clients() {
    return this._clients
  }

  get tableStore() {
    return this._tableStore
  }

  get items() {
    return Array.from(this._listMap.values())
  }

  get clientModalId() {
    return this._clientModalId
  }

  get editingClientId() {
    return this._editingClientId
  }

  get clientModalTitle() {
    return this._editingClientId ? 'Edit client' : 'New client'
  }

  get clientModalPrimaryButtonText() {
    return this._editingClientId ? 'Save' : 'Create'
  }

  get clientName() {
    return this._newClientName
  }

  get clientRedirectUrl() {
    return this._newClientRedirectUrl
  }

  get clientNameError() {
    return this._newClientNameError
  }

  get clientRedirectUrlError() {
    return this._newClientRedirectUrlError
  }

  toggleScope = (scope: string) => {
    if (this.selectedScopes.includes(scope)) {
      this.selectedScopes = this.selectedScopes.filter((selectedScopes) => selectedScopes !== scope)
    } else {
      this.selectedScopes = [...this.selectedScopes, scope]
    }
  }

  get isClientFormValid(): boolean {
    const name = this._newClientName.trim()
    const redirect = this._newClientRedirectUrl.trim()

    return Boolean(name && redirect && this._isValidUrl(redirect))
  }

  get clientModalSaving() {
    return this._clientModalSaving
  }

  setNewClientName = (value: string) => {
    this._newClientName = value
    if (value.trim()) {
      this._newClientNameError = ''
    }
  }

  validateNewClientNameBlur = () => {
    if (!this._newClientName.trim()) {
      this._newClientNameError = 'Please enter a client name'
    } else {
      this._newClientNameError = ''
    }
  }

  setNewClientRedirectUrl = (value: string) => {
    this._newClientRedirectUrl = value
    if (this._isValidUrl(value)) {
      this._newClientRedirectUrlError = ''
    }
  }

  validateNewClientRedirectUrlBlur = () => {
    const trimmed = this._newClientRedirectUrl.trim()
    if (!trimmed || !this._isValidUrl(trimmed)) {
      this._newClientRedirectUrlError = 'Please enter a valid URL'
    } else {
      this._newClientRedirectUrlError = ''
    }
  }

  clearCreateClientError = () => {
    this.createClientError = ''
  }

  private _isValidUrl = (value: string): boolean => {
    const trimmed = value.trim()
    if (!trimmed) return false
    try {
      const url = new URL(trimmed)
      return url.protocol === 'http:' || url.protocol === 'https:'
    } catch {
      return false
    }
  }

  openNewClientModal = () => {
    this._editingClientId = null
    this._resetNewClientForm()
    this.selectedScopes = this.allScopes
    this.createClientError = ''
    this._openClientModal()
  }

  openEditClientModal = (client: IResponseAuthClient) => {
    this._editingClientId = client.id
    this._newClientName = client.name
    this._newClientRedirectUrl = client.redirect ?? ''
    this._newClientNameError = ''
    this._newClientRedirectUrlError = ''
    this.createClientError = ''
    this.selectedScopes = client.allowed_scopes?.length ? client.allowed_scopes : this.allScopes
    this._openClientModal()
  }

  private _openClientModal = () => {
    modalStore.addModal({
      id: this._clientModalId,
      type: ModalTypeList.DEFAULT,
      paddingContent: '8px 0 16px 24px',
      width: 480,
      title: this.clientModalTitle,
      showCloseButton: true,
      closeText: 'Cancel',
      ModalContent: ClientModalContent,
      ModalContentProps: { store: this },
      ModalActions: ClientModalActions,
      secondaryAction: {
        text: 'Cancel',
        onAction: () => this.closeClientModal(),
      },
      onClose: () => this.closeClientModal(),
    })
  }

  closeClientModal = () => {
    modalStore.removeModal(this._clientModalId)
    this._editingClientId = null
    this.selectedScopes = this.allScopes
    this.createClientError = ''
    this._resetNewClientForm()
  }

  openRevokeModal = (client: IResponseAuthClient) => {
    this._revokingClientId = client.id
    this._revoking = false
    modalStore.addModal({
      id: this._revokeModalId,
      type: ModalTypeList.ALERT,
      width: 480,
      title: 'Revoke client?',
      desc: 'This action cannot be undone',
      showCloseButton: true,
      closeText: 'Cancel',
      primaryAction: {
        text: 'Yes, revoke',
        disabled: false,
        onAction: () => this.handleRevokeAuthClient(),
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => this.closeRevokeModal(),
      },
      onClose: () => this.closeRevokeModal(),
    })
    this._revokeModalDisposeReaction?.()
    this._revokeModalDisposeReaction = reaction(
      () => this._revoking,
      (revoking) => {
        const modal = modalStore.getModal(this._revokeModalId)
        if (modal && 'primaryAction' in modal && modal.primaryAction) {
          modalStore.removeModal(this._revokeModalId)
          modalStore.addModal({
            ...modal,
            primaryAction: {
              ...modal.primaryAction,
              disabled: revoking,
            },
          })
        }
      }
    )
  }

  closeRevokeModal = () => {
    this._revokeModalDisposeReaction?.()
    this._revokeModalDisposeReaction = null
    modalStore.removeModal(this._revokeModalId)
    this._revokingClientId = null
    this._revoking = false
  }

  handleRevokeAuthClient = async () => {
    const id = this._revokingClientId
    if (!id) return

    this._revoking = true
    try {
      await DeveloperAuthApi.revokeAuthClient(id)
      this.closeRevokeModal()
      showToast({ type: 'success', title: 'Client revoked' })
      await this.loadClients()
    } catch {
      showToast({ type: 'error', title: 'Failed to revoke client' })
    } finally {
      this._revoking = false
    }
  }

  handleSaveAuthClient = async () => {
    if (!this.selectedScopes.length) {
      this.createClientError = 'Please select at least one scope'
      return
    }
    this.createClientError = ''

    if (this._editingClientId) {
      await this.handleUpdateAuthClient()
      return
    }
    await this.handleCreateAuthClient()
  }

  handleCreateAuthClient = async () => {
    const name = this._newClientName.trim()
    const redirect = this._newClientRedirectUrl.trim()

    this._clientModalSaving = true
    try {
      await DeveloperAuthApi.createAuthClient({
        name,
        redirect,
        allowed_scopes: this.selectedScopes,
      })

      this.closeClientModal()

      showToast({ type: 'success', title: 'Client created' })

      await this.loadClients()
    } catch (error) {
      if (isAxiosError<IResponseCreateAuthClientError>(error)) {
        const errorData = error.response?.data
        if (errorData?.redirect?.length) {
          this._newClientRedirectUrlError = 'Please enter a valid URL'
          return
        }
      }
      showToast({ type: 'error', title: 'Failed to create a new client' })
    } finally {
      this._clientModalSaving = false
    }
  }

  handleUpdateAuthClient = async () => {
    const id = this._editingClientId
    if (!id) return

    const name = this._newClientName.trim()
    const redirect = this._newClientRedirectUrl.trim()

    this._clientModalSaving = true
    try {
      await DeveloperAuthApi.updateAuthClient(id, {
        name,
        redirect,
        allowed_scopes: this.selectedScopes,
      })

      this.closeClientModal()

      showToast({ type: 'success', title: 'Client saved' })

      await this.loadClients()
    } catch (error) {
      if (isAxiosError<IResponseCreateAuthClientError>(error)) {
        const errorData = error.response?.data
        if (errorData?.redirect?.length) {
          this._newClientRedirectUrlError = 'Please enter a valid URL'
          return
        }
      }
      showToast({ type: 'error', title: 'Failed to update client' })
    } finally {
      this._clientModalSaving = false
    }
  }

  private _resetNewClientForm = () => {
    this._newClientName = ''
    this._newClientRedirectUrl = ''
    this._newClientNameError = ''
    this._newClientRedirectUrlError = ''
    this.selectedScopes = this.allScopes
  }

  dispose = () => {
    this._revokeModalDisposeReaction?.()
    this._revokeModalDisposeReaction = null
    modalStore.removeModal(this._revokeModalId)
    modalStore.removeModal(this._clientModalId)
  }
}
